export interface DeviceDelivery {
  readonly expiresAt?: string;
  readonly shareId: string;
}

export type DeviceConnection =
  | {
    readonly connectionToken: string;
    readonly shareId: string;
    readonly status: "ready";
  }
  | { readonly status: "verification-required" }
  | { readonly status: "unavailable" };

export type AccountDeviceConnection =
  | {
    readonly account?: {
      readonly avatarUrl?: string;
      readonly displayName?: string;
      readonly username?: string;
    };
    readonly connectionToken: string;
    readonly shareId: string;
    readonly status: "ready";
  }
  | { readonly status: "unavailable" }
  | { readonly status: "account-mismatch" }
  | { readonly status: "sign-in-required" };

export async function requestInvitationCode(token: string): Promise<{
  readonly emailHint: string;
}> {
  const value = await requestJson("/api/invites/verification", {
    body: JSON.stringify({ token }),
    headers: { "Content-Type": "application/json" },
    method: "POST",
  });
  const record = requireRecord(value);
  return {
    emailHint: typeof record.emailHint === "string"
      ? record.emailHint
      : "your verified email",
  };
}

export async function acceptInvitation(
  token: string,
  code: string,
): Promise<DeviceDelivery | null> {
  const value = await requestJson("/api/invites/accept", {
    body: JSON.stringify({ code, platform: "web", token }),
    headers: { "Content-Type": "application/json" },
    method: "POST",
  });
  return deliveryFromUnknown(value);
}

export async function createDeviceConnection(
  invitationToken: string,
): Promise<DeviceConnection> {
  const value = await requestJson("/api/device/connections", {
    body: JSON.stringify({
      invitationToken,
    }),
    headers: { "Content-Type": "application/json" },
    method: "POST",
  });
  const record = requireRecord(value);
  if (record.status === "verification-required") {
    return { status: "verification-required" };
  }
  if (record.status !== "ready") return { status: "unavailable" };
  if (
    typeof record.connectionToken !== "string"
    || record.connectionToken.length < 20
  ) {
    throw new Error("Connection authorization is unavailable");
  }
  const grants = Array.isArray(record.grants) ? record.grants : [];
  const shareId = grants
    .map((grant) => (
      grant && typeof grant === "object" && !Array.isArray(grant)
        ? (grant as Record<string, unknown>).shareId
        : null
    ))
    .find((value): value is string => typeof value === "string");
  if (!shareId) {
    throw new Error("Connection authorization is unavailable");
  }
  return {
    connectionToken: record.connectionToken,
    shareId,
    status: "ready",
  };
}

export async function createAccountDeviceConnection(
  shareId: string,
): Promise<AccountDeviceConnection> {
  const response = await fetch("/api/device/connections", {
    body: JSON.stringify({ platform: "web", shareId }),
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    method: "POST",
  });
  if (response.status === 401) return { status: "sign-in-required" };
  if (response.status === 403) return { status: "account-mismatch" };
  const value = await response.json().catch(() => null);
  if (!response.ok) return { status: "unavailable" };
  const record = requireRecord(value);
  if (
    typeof record.connectionToken !== "string"
    || record.connectionToken.length < 20
  ) {
    throw new Error("Connection authorization is unavailable");
  }
  const grants = Array.isArray(record.grants) ? record.grants : [];
  const hasRequestedGrant = grants.some((grant) => (
    grant
    && typeof grant === "object"
    && !Array.isArray(grant)
    && (grant as Record<string, unknown>).shareId === shareId
  ));
  if (!hasRequestedGrant) {
    throw new Error("Connection authorization is unavailable");
  }
  return {
    account: accountFromUnknown(record.account),
    connectionToken: record.connectionToken,
    shareId,
    status: "ready",
  };
}

export async function recordAlbumDownloadCompletion(
  shareId: string,
  connectionToken: string,
): Promise<void> {
  await requestJson(
    `/api/shares/${encodeURIComponent(shareId)}/downloads/complete`,
    {
      headers: {
        Authorization: `Bearer ${connectionToken}`,
      },
      method: "POST",
    },
  );
}

export async function deleteViewerPhoto(
  shareId: string,
  mediaId: string,
  connectionToken: string,
): Promise<void> {
  await requestJson(
    `/api/shares/${encodeURIComponent(shareId)}/storage/media/${encodeURIComponent(mediaId)}`,
    {
      headers: {
        Authorization: `Bearer ${connectionToken}`,
      },
      method: "DELETE",
    },
  );
}

function deliveryFromUnknown(value: unknown): DeviceDelivery | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const root = value as Record<string, unknown>;
  const candidates = [
    root.delivery,
    root.viewerSession,
    root.session,
    root,
  ];
  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) continue;
    const record = candidate as Record<string, unknown>;
    if (typeof record.shareId === "string") {
      return {
        expiresAt: typeof record.expiresAt === "string" ? record.expiresAt : undefined,
        shareId: record.shareId,
      };
    }
  }
  return null;
}

async function requestJson(path: string, init: RequestInit): Promise<unknown> {
  const response = await fetch(path, { credentials: "same-origin", ...init });
  const value = await response.json().catch(() => null);
  if (!response.ok) {
    const record = value && typeof value === "object"
      ? value as Record<string, unknown>
      : null;
    throw new Error(
      typeof record?.error === "string"
        ? record.error
        : "AasaanShare is unavailable right now",
    );
  }
  return value;
}

function requireRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error("The service response is invalid");
  }
  return value as Record<string, unknown>;
}

function accountFromUnknown(value: unknown): {
  readonly avatarUrl?: string;
  readonly displayName?: string;
  readonly username?: string;
} | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const record = value as Record<string, unknown>;
  const account = {
    ...(typeof record.avatarUrl === "string" && record.avatarUrl
      ? { avatarUrl: record.avatarUrl }
      : {}),
    ...(typeof record.displayName === "string" && record.displayName
      ? { displayName: record.displayName }
      : {}),
    ...(typeof record.username === "string" && record.username
      ? { username: record.username }
      : {}),
  };
  return Object.keys(account).length > 0 ? account : undefined;
}
